import {KeycloakConfig, UaaLogoutConfig} from './interfaces';

const KEYCLOAK_LOGOUT_PATH_SEGMENT = 'protocol/openid-connect/logout';

/**
 * Builds the Keycloak realm logout path relative to the /auth/ base URL.
 *
 * @param realm - Keycloak realm name
 * @returns Relative logout path, e.g. realms/raam/protocol/openid-connect/logout
 */
function buildKeycloakLogoutPath(realm: string): string {
    return `realms/${encodeURIComponent(realm)}/${KEYCLOAK_LOGOUT_PATH_SEGMENT}`;
}

/**
 * Returns the portal root URL used as a safe post-logout fallback.
 *
 * @returns Same-origin portal root URL, e.g. http://localhost:4200/
 */
export function getPortalRootPostLogoutRedirectUri(): string {
    return validatePortalPostLogoutRedirectUri(`${globalThis.location.origin}/`);
}

/**
 * Validates that a post-logout redirect URI stays on the current portal origin.
 *
 * @param redirectUri - Candidate redirect URI
 * @returns Sanitized redirect URI on the portal origin
 */
export function validatePortalPostLogoutRedirectUri(redirectUri: string): string {
    const fallback = `${globalThis.location.origin}/`;
    let parsed: URL;

    try {
        parsed = new URL(redirectUri, globalThis.location.origin);
    } catch {
        return fallback;
    }

    if (parsed.origin !== globalThis.location.origin) {
        return fallback;
    }

    if (!['http:', 'https:'].includes(parsed.protocol)) {
        return fallback;
    }

    return parsed.href;
}

/**
 * Checks whether the configured UAA logout endpoint is usable.
 *
 * @param config - UAA logout configuration
 * @returns True when the endpoint is a valid HTTPS (or local HTTP) URL
 */
export function isUaaLogoutConfigured(config?: UaaLogoutConfig): config is UaaLogoutConfig {
    if (!config?.endSessionEndpoint || !config.clientId) {
        return false;
    }

    try {
        validateUaaEndSessionEndpoint(config.endSessionEndpoint);
        return true;
    } catch {
        return false;
    }
}

/**
 * Validates the UAA end-session endpoint from configuration.
 *
 * @param endSessionEndpoint - UAA logout URL from config
 * @returns Parsed endpoint URL
 * @throws Error when the endpoint is invalid or uses an insecure protocol
 */
export function validateUaaEndSessionEndpoint(endSessionEndpoint: string): URL {
    const endpoint = new URL(endSessionEndpoint);

    if (endpoint.protocol === 'https:') {
        return endpoint;
    }

    if (endpoint.protocol === 'http:' && isLocalhost(endpoint.hostname)) {
        return endpoint;
    }

    throw new Error('UAA logout endpoint must use HTTPS');
}

/**
 * Builds the Keycloak RP-initiated logout URL used after UAA logout completes.
 *
 * @param keycloak - Keycloak client configuration
 * @param postLogoutRedirectUri - Portal URL validated to the current origin
 * @param idTokenHint - Optional Keycloak ID token for session matching
 * @returns Keycloak logout URL
 */
export function buildKeycloakLogoutUrl(
    keycloak: KeycloakConfig,
    postLogoutRedirectUri: string,
    idTokenHint?: string,
    keycloakBaseUrl?: string
): string {
    const keycloakBase = normalizeKeycloakBaseUrl(keycloakBaseUrl ?? keycloak.url);
    const logoutUrl = new URL(buildKeycloakLogoutPath(keycloak.realm), keycloakBase);

    logoutUrl.searchParams.set('client_id', keycloak.clientId);
    logoutUrl.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri);

    if (idTokenHint && isIdTokenHintCompatibleWithKeycloakUrl(idTokenHint, keycloakBase.href)) {
        logoutUrl.searchParams.set('id_token_hint', idTokenHint);
    }

    return logoutUrl.href;
}

/**
 * Builds the UAA front-channel logout URL that redirects directly to Keycloak logout.
 *
 * UAA post_logout_redirect_uri must be allowlisted on the UAA client as the browser-facing
 * Keycloak logout endpoint (path only; query params carry the final portal destination).
 *
 * @param uaaLogout - UAA logout configuration
 * @param keycloak - Keycloak client configuration
 * @param portalPostLogoutRedirectUri - Same-origin portal URL after Keycloak logout completes
 * @param idTokenHint - Optional Keycloak ID token to skip Keycloak logout confirmation
 * @returns UAA logout URL
 */
export function buildUaaLogoutUrl(
    uaaLogout: UaaLogoutConfig,
    keycloak: KeycloakConfig,
    portalPostLogoutRedirectUri: string,
    idTokenHint?: string
): string {
    const endpoint = validateUaaEndSessionEndpoint(uaaLogout.endSessionEndpoint);
    const keycloakLogoutUrl = buildKeycloakLogoutUrl(
        keycloak,
        validatePortalPostLogoutRedirectUri(portalPostLogoutRedirectUri),
        idTokenHint,
        uaaLogout.keycloakUrl
    );

    validateKeycloakLogoutRedirectTarget(keycloakLogoutUrl, uaaLogout.keycloakUrl);

    const logoutUrl = new URL(endpoint.href);
    logoutUrl.searchParams.set('client_id', uaaLogout.clientId);
    logoutUrl.searchParams.set('post_logout_redirect_uri', keycloakLogoutUrl);

    return logoutUrl.href;
}

/**
 * Checks whether an ID token issuer matches the browser-facing Keycloak base URL.
 *
 * @param idTokenHint - Keycloak ID token
 * @param keycloakBaseUrl - Browser-facing Keycloak base URL
 * @returns True when the token issuer matches the configured Keycloak URL
 */
export function isIdTokenHintCompatibleWithKeycloakUrl(idTokenHint: string, keycloakBaseUrl: string): boolean {
    try {
        const payload = parseJwtPayload(idTokenHint);
        const issuer = String(payload.iss ?? '');
        const issuerKeycloakBase = issuer.replace(/\/realms\/[^/]+\/?$/, '/');
        const configuredKeycloakBase = normalizeKeycloakBaseUrl(keycloakBaseUrl).href;

        return normalizeUrlPrefix(issuerKeycloakBase) === normalizeUrlPrefix(configuredKeycloakBase);
    } catch {
        return false;
    }
}

/**
 * Ensures the UAA post-logout redirect target is the configured Keycloak logout endpoint.
 *
 * @param redirectTarget - Redirect URI passed to UAA
 * @param keycloakUrl - Expected Keycloak base URL from configuration
 */
export function validateKeycloakLogoutRedirectTarget(redirectTarget: string, keycloakUrl: string): void {
    const parsedTarget = new URL(redirectTarget);
    const keycloakBase = normalizeKeycloakBaseUrl(keycloakUrl);

    if (parsedTarget.origin !== keycloakBase.origin) {
        throw new Error('UAA post-logout redirect must target configured Keycloak origin');
    }

    if (!parsedTarget.pathname.includes(`realms/`) || !parsedTarget.pathname.includes(KEYCLOAK_LOGOUT_PATH_SEGMENT)) {
        throw new Error('UAA post-logout redirect must target Keycloak logout endpoint');
    }
}

function normalizeKeycloakBaseUrl(keycloakUrl: string): URL {
    return new URL(keycloakUrl.endsWith('/') ? keycloakUrl : `${keycloakUrl}/`);
}

function isLocalhost(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

function normalizeUrlPrefix(url: string): string {
    return url.endsWith('/') ? url : `${url}/`;
}

function parseJwtPayload(idToken: string): { iss?: string } {
    const encodedPayload = idToken.split('.')[1];
    if (!encodedPayload) {
        throw new Error('Invalid ID token');
    }

    const normalizedPayload = encodedPayload.replaceAll('-', '+').replaceAll('_', '/');
    return JSON.parse(atob(normalizedPayload)) as { iss?: string };
}
